// ========================================
// Messages View - Internal Email System
// ========================================

const MessagesView = {
    currentFolder: 'inbox',
    selectedMessageId: null,
    searchQuery: '',
    isComposing: false,
    isReadingFull: false,
    currentAttachments: [],
    sidebarWidth: parseInt(BrowserState.getItem('messages_sidebar_width') || '180', 10),
    listWidth: parseInt(BrowserState.getItem('messages_list_width') || '280', 10),
    pollingTimer: null,
    pollingIntervalMs: 6000,

    init() {
        console.log('Initializing MessagesView with auto-refresh...');
        this.startAutoPolling();
    },

    destroy() {
        this.stopAutoPolling();
    },

    startAutoPolling() {
        this.stopAutoPolling();
        this.pollingTimer = setInterval(async () => {
            // Solo actuar si seguimos dentro de la sección 'mensajes'
            if (typeof App !== 'undefined' && App.currentView === 'mensajes') {
                await this.checkAndReloadMessages();
            } else {
                this.stopAutoPolling();
            }
        }, this.pollingIntervalMs);
    },

    stopAutoPolling() {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    },

    async checkAndReloadMessages() {
        // No refrescar si el usuario está redactando un correo para no perder borradores
        if (this.isComposing) return;

        try {
            // Cheap fingerprint first; skip the full download when nothing changed.
            const state = await FileStorage.fetchCommunicationsState();
            if (!FileStorage.communicationsChanged(state, 'messages')) return;

            // null while a local change is being saved: try again on the next poll.
            const newMessages = await FileStorage.fetchCommunicationList('messages');
            if (!newMessages) return;
            FileStorage.markCommunicationsSynced('messages', state);
            const currentMessages = (typeof FileStorage !== 'undefined' && FileStorage.dataStore.messages) ? FileStorage.dataStore.messages : [];

            // Comparar si hubo cambios en los mensajes
            const oldHash = JSON.stringify(currentMessages);
            const newHash = JSON.stringify(newMessages);

            if (oldHash !== newHash) {
                // Actualizar store y BrowserState
                if (typeof FileStorage !== 'undefined') {
                    FileStorage.dataStore.messages = newMessages;
                    BrowserState.setItem('ecpv_messages', JSON.stringify(newMessages));
                }

                // Actualizar contador del encabezado general
                if (typeof App !== 'undefined' && App.setupHeaderInfo) {
                    App.setupHeaderInfo();
                }

                // Actualizar insignia de no leídos en la barra lateral de carpetas
                this.updateFolderBadges();

                // Actualizar la lista de mensajes sin interrumpir lo que el usuario esté leyendo
                const listContainer = document.querySelector('.message-items-container');
                if (listContainer) {
                    SafeHTML.set(listContainer, this.renderMessageItemsOnly());
                }
            }
        } catch (e) {
            // Silencioso
        }
    },

    getFolderCounts() {
        const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (!currentUser) return { unreadCount: 0, trashCount: 0 };
        const unreadCount = DataManager.getUnreadCount(currentUser.username);
        const allMessages = DataManager.getMessages();
        const isTrashed = (m, u) => (Array.isArray(m.trashFor) && m.trashFor.includes(u)) || (!m.trashFor && m.trash);
        const isDeleted = (m, u) => Array.isArray(m.deletedFor) && m.deletedFor.includes(u);
        const trashCount = allMessages.filter(m => (m.senderId === currentUser.username || m.receiverId === currentUser.username) && isTrashed(m, currentUser.username) && !isDeleted(m, currentUser.username)).length;
        return { unreadCount, trashCount };
    },

    updateFolderBadges() {
        const { unreadCount, trashCount } = this.getFolderCounts();
        const inboxLink = document.querySelector('.message-folders a.folder-item[data-folder="inbox"]');
        if (inboxLink) {
            let badge = inboxLink.querySelector('.unread-badge');
            if (unreadCount > 0) {
                if (badge) {
                    badge.textContent = unreadCount;
                } else {
                    SafeHTML.insert(inboxLink, 'beforeend', `<span class="unread-badge">${unreadCount}</span>`);
                }
            } else if (badge) {
                badge.remove();
            }
        }
        const trashLink = document.querySelector('.message-folders a.folder-item[data-folder="trash"]');
        if (trashLink) {
            let badge = trashLink.querySelector('.count-badge');
            if (trashCount > 0) {
                if (badge) {
                    badge.textContent = trashCount;
                } else {
                    SafeHTML.insert(trashLink, 'beforeend', `<span class="count-badge" style="background:#64748b; color:white; font-size:0.75rem; padding:1px 6px; border-radius:10px; margin-left:auto; font-weight:600;">${trashCount}</span>`);
                }
            } else if (badge) {
                badge.remove();
            }
        }
    },

    render() {
        // If composing, show full-width compose form
        if (this.isComposing) {
            return `
                <div class="messages-view-wrapper dnr-proposal msgc">
                    <div class="messages-container">
                        ${this.renderSidebar()}
                        <div class="messages-resizer" id="resizer-sidebar" title="Arrastra para ajustar el ancho"></div>
                        ${this.renderComposeFormFullWidth()}
                    </div>
                </div>
            `;
        }

        // If reading in full mode
        if (this.isReadingFull && this.selectedMessageId) {
            return `
                <div class="messages-view-wrapper dnr-proposal msgc">
                    <div class="messages-container">
                        ${this.renderSidebar()}
                        <div class="messages-resizer" id="resizer-sidebar" title="Arrastra para ajustar el ancho"></div>
                        ${this.renderFullContent()}
                    </div>
                </div>
            `;
        }

        return `
            <div class="messages-view-wrapper dnr-proposal msgc">
                <div class="messages-container">
                    ${this.renderSidebar()}
                    <div class="messages-resizer" id="resizer-sidebar" title="Arrastra para ajustar las carpetas"></div>
                    ${this.renderList()}
                    <div class="messages-resizer" id="resizer-list" title="Arrastra para ajustar la lista de mensajes"></div>
                    ${this.renderContent()}
                </div>
            </div>
        `;
    },

    renderSidebar() {
        const currentUser = AuthManager.getCurrentUser();
        const { unreadCount, trashCount } = this.getFolderCounts();

        return `
            <aside class="messages-sidebar" style="width: ${this.sidebarWidth}px;">
                <h1 class="msgc-title">Mensajes</h1>
                <div class="compose-btn-wrapper">
                    <button class="btn btn-primary compose-btn" ${UIEvents.attr("click", function(event,captured){MessagesView.openComposeModal();}, [])}>
                        <i class="fas fa-plus"></i> Redactar
                    </button>
                </div>
                
                <div class="message-folders">
                    <a href="/mensajes" data-folder="inbox" class="folder-item ${this.currentFolder === 'inbox' ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){event.preventDefault();MessagesView.switchFolder('inbox');}, [])}>
                        <i class="fas fa-inbox"></i>
                        <span>Recibidos</span>
                        ${unreadCount > 0 ? `<span class="unread-badge">${unreadCount}</span>` : ''}
                    </a>
                    <a href="/mensajes" data-folder="sent" class="folder-item ${this.currentFolder === 'sent' ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){event.preventDefault();MessagesView.switchFolder('sent');}, [])}>
                        <i class="fas fa-paper-plane"></i>
                        <span>Enviados</span>
                    </a>
                    <a href="/mensajes" data-folder="important" class="folder-item ${this.currentFolder === 'important' ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){event.preventDefault();MessagesView.switchFolder('important');}, [])}>
                        <i class="fas fa-star"></i>
                        <span>Destacados</span>
                    </a>
                    <a href="/mensajes" data-folder="trash" class="folder-item ${this.currentFolder === 'trash' ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){event.preventDefault();MessagesView.switchFolder('trash');}, [])}>
                        <i class="fas fa-trash"></i>
                        <span>Papelera</span>
                        ${trashCount > 0 ? `<span class="count-badge">${trashCount}</span>` : ''}
                    </a>
                </div>
                <button type="button" class="msgc-directory" ${UIEvents.attr("click", function(event,captured){MessagesView.openDirectoryFromSidebar();}, [])}>
                    <i class="fas fa-address-book" aria-hidden="true"></i> Directorio de contactos
                </button>
            </aside>
        `;
    },

    renderList() {
        return `
            <div class="messages-list" style="width: ${this.listWidth}px;">
                <div class="list-header">
                    <h2>${this.getFolderTitle()}</h2>
                    <div class="list-actions" style="display: flex; align-items: center; gap: 0.5rem;">
                        ${this.currentFolder === 'trash' ? `
                            <button class="btn btn-sm btn-outline-danger" ${UIEvents.attr("click", function(event,captured){MessagesView.emptyTrash();}, [])} title="Vaciar papelera" style="font-size: 0.75rem; padding: 0.25rem 0.6rem; display: inline-flex; align-items: center; gap: 0.35rem; border-radius: 6px;">
                                <i class="fas fa-trash-alt"></i> Vaciar
                            </button>
                        ` : ''}
                        <button class="icon-button" ${UIEvents.attr("click", function(event,captured){MessagesView.refresh();}, [])} title="Refrescar mensajes" aria-label="Refrescar mensajes"><i class="fas fa-sync-alt"></i></button>
                    </div>
                </div>
                <div class="msgc-search">
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <input type="search" id="messages-search" placeholder="Buscar en ${this.getFolderTitle().toLowerCase()}" aria-label="Buscar mensajes" value="${Utils.sanitizeHTML(this.searchQuery || '')}"
                        ${UIEvents.attr("input", function(event,captured){MessagesView.setSearch(this.value);}, [])}>
                </div>
                ${this.currentFolder === 'trash' ? `
                    <div style="background: rgba(241, 245, 249, 0.95); border-bottom: 1px solid var(--border-color); padding: 0.45rem 0.75rem; font-size: 0.72rem; color: var(--text-secondary); display: flex; align-items: center; gap: 0.45rem; line-height: 1.3;">
                        <i class="fas fa-clock-rotate-left" style="color: #64748b; font-size: 0.75rem; flex-shrink: 0;"></i>
                        <span>Los correos se eliminan automáticamente a los 30 días de ir a la papelera.</span>
                    </div>
                ` : ''}
                <div class="message-items-container">
                    ${this.renderMessageItemsOnly()}
                </div>
            </div>
        `;
    },

    renderMessageItemsOnly() {
        const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (!currentUser) return '<div style="padding: 3rem; text-align: center; color: var(--text-tertiary);">Inicia sesión</div>';

        let messages = typeof FileStorage !== 'undefined'
            ? FileStorage.visibleCommunications('messages', this.currentFolder, this.folderMessages(currentUser.username))
            : this.folderMessages(currentUser.username);
        const loadMore = typeof FileStorage !== 'undefined' && FileStorage.hasMoreCommunications('messages', this.currentFolder)
            ? `<button type="button" class="messages-load-more" ${UIEvents.attr("click", function(event,captured){MessagesView.loadMore(this);}, [])}><i class="fas fa-angle-down" aria-hidden="true"></i> Cargar más</button>`
            : '';

        // Apply Search
        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            messages = messages.filter(m =>
                (m.subject || '').toLowerCase().includes(query) ||
                (m.senderName || '').toLowerCase().includes(query) ||
                (m.content || '').toLowerCase().includes(query)
            );
        }

        if (messages.length === 0) {
            const emptyLabel = this.searchQuery ? 'Ningún mensaje cargado coincide' : this.currentFolder === 'trash' ? 'La papelera está vacía' : 'No hay mensajes';
            return `<div style="padding: 3rem; text-align: center; color: var(--text-tertiary);">${emptyLabel}</div>${loadMore}`;
        }

        return messages.map(m => this.renderMessageItem(m)).join('') + loadMore;
    },

    /** Loaded messages of the open folder, newest first (same rules as the server pages). */
    folderMessages(username) {
        const isTrashed = m => (Array.isArray(m.trashFor) && m.trashFor.includes(username)) || (!m.trashFor && m.trash);
        const isDeleted = m => Array.isArray(m.deletedFor) && m.deletedFor.includes(username);
        const mine = m => m.senderId === username || m.receiverId === username;
        const rules = {
            inbox: m => m.receiverId === username && !isTrashed(m),
            sent: m => m.senderId === username && !isTrashed(m),
            important: m => mine(m) && m.important && !isTrashed(m),
            trash: m => mine(m) && isTrashed(m)
        };
        const rule = rules[this.currentFolder] || (() => false);
        return DataManager.getMessages().filter(m => rule(m) && !isDeleted(m))
            .sort((a, b) => new Date(b.date) - new Date(a.date) || (a.id < b.id ? 1 : -1));
    },

    async loadMore(button) {
        const user = AuthManager.getCurrentUser();
        if (!user || button.disabled) return;
        button.disabled = true;
        const folder = this.currentFolder;
        const ok = await FileStorage.loadMoreCommunications('messages', folder);
        if (!ok) {
            button.disabled = false;
            Utils.showToast('No se pudieron cargar más mensajes. Revisa la conexión.', 'error');
            return;
        }
        if (this.currentFolder !== folder) return;
        const list = document.querySelector('.message-items-container');
        if (list) SafeHTML.set(list, this.renderMessageItemsOnly());
    },

    renderMessageItem(msg) {
        const isActive = this.selectedMessageId === msg.id;
        const isUnread = !msg.read && msg.receiverId === AuthManager.getCurrentUser().username;
        const date = new Date(msg.date);
        const dateLabel = date.toLocaleDateString() === new Date().toLocaleDateString() ?
            date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
            date.toLocaleDateString([], { day: '2-digit', month: 'short' });

        const isTrashView = this.currentFolder === 'trash';
        let trashBadge = '';
        if (isTrashView) {
            const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
            const u = currentUser?.username;
            const trashedDateStr = (msg.trashedDates && u && msg.trashedDates[u]) ? msg.trashedDates[u] : msg.date;
            const trashedTime = new Date(trashedDateStr).getTime();
            const daysPassed = Math.floor((Date.now() - trashedTime) / (24 * 60 * 60 * 1000));
            const daysLeft = Math.max(1, 30 - daysPassed);
            trashBadge = `<span class="trash-expiry-badge" style="font-size: 0.68rem; color: #b91c1c; background: #fee2e2; padding: 1px 5px; border-radius: 4px; font-weight: 600;" title="Días restantes antes del borrado definitivo">Expira en ${daysLeft}d</span>`;
        }

        return `
            <div class="message-item ${isActive ? 'active' : ''} ${isUnread ? 'unread' : ''}" 
                 data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.selectMessage(this.dataset.messageId);}, [])}
                 ${UIEvents.attr("dblclick", function(event,captured){MessagesView.openFullView(this.dataset.messageId);}, [])}>
                <div class="msg-meta">
                    <span class="sender-name">
                        ${isUnread ? '<span class="unread-dot"></span>' : ''}${Utils.sanitizeHTML(msg.senderName)}
                    </span>
                    <div class="msg-meta-right">
                        ${trashBadge}
                        <span class="msg-date">${dateLabel}</span>
                        ${isTrashView ? `
                            <button class="msg-restore-btn" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){event.stopPropagation();MessagesView.restoreMessage(this.dataset.messageId);}, [])} title="Restaurar a su bandeja">
                                <i class="fas fa-undo"></i>
                            </button>
                            <button class="msg-delete-btn" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){event.stopPropagation();MessagesView.deletePermanently(this.dataset.messageId);}, [])} title="Eliminar definitivamente">
                                <i class="fas fa-trash-alt"></i>
                            </button>
                        ` : `
                            <button class="msg-delete-btn" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){event.stopPropagation();MessagesView.deleteMessage(this.dataset.messageId);}, [])} title="Mover a la papelera">
                                <i class="fas fa-trash"></i>
                            </button>
                        `}
                    </div>
                </div>
                <div class="msg-subject">${Utils.sanitizeHTML(msg.subject || '(Sin asunto)')}</div>
                ${msg.content ? `<div class="msg-preview">${Utils.sanitizeHTML(msg.content)}</div>` : ''}
            </div>
        `;
    },

    renderContent() {
        // If composing, show compose form
        if (this.isComposing) {
            return this.renderComposeForm();
        }

        if (!this.selectedMessageId) {
            return `
                <div class="message-content">
                    <div class="content-empty">
                        <i class="far fa-envelope-open"></i>
                        <p class="content-empty-title">Selecciona un mensaje para leerlo</p>
                        <span class="content-empty-sub">Elige una conversación de la lista para ver su contenido</span>
                    </div>
                </div>
            `;
        }

        const msg = DataManager.getMessage(this.selectedMessageId);
        if (!msg) {
            return `
                <div class="message-content">
                    <div class="content-empty">
                        <i class="fas fa-exclamation-circle"></i>
                        <p class="content-empty-title">Mensaje no encontrado</p>
                    </div>
                </div>
            `;
        }

        return `
            <div class="message-content">
                <div class="message-scroll-area">
                    ${this.renderMessageCard(msg)}
                </div>
            </div>
        `;
    },

    // Marcado del mensaje (cabecera + cuerpo + adjuntos) compartido entre la
    // vista de 3 paneles (renderContent) y la vista a pantalla completa
    // (renderFullContent) — antes eran dos copias casi idénticas que había
    // que mantener sincronizadas a mano.
    renderMessageCard(msg) {
        const date = new Date(msg.date).toLocaleString([], {
            day: '2-digit', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });

        return `
            ${this.currentFolder === 'trash' ? `
                <div class="trash-notice">
                    <div class="trash-notice-text">
                        <i class="fas fa-clock-rotate-left"></i>
                        <span>Este correo se eliminará automáticamente a los 30 días de haber ido a la papelera.</span>
                    </div>
                    <button class="btn btn-sm btn-secondary" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.restoreMessage(this.dataset.messageId);}, [])}>
                        <i class="fas fa-undo"></i> Restaurar
                    </button>
                </div>
            ` : ''}
            <div class="message-view-card">
                <div class="content-header">
                    <div class="content-header-top">
                        <h1 class="subject-large">${Utils.sanitizeHTML(msg.subject)}</h1>
                    </div>
                    <div class="sender-detail">
                        <div class="sender-detail-main">
                            <div class="sender-avatar clickable-author" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.composeToSender(this.dataset.messageId);}, [])} title="Redactar nuevo mensaje a ${Utils.sanitizeHTML(msg.senderName)}">${Utils.sanitizeHTML(String(msg.senderName || "").charAt(0).toUpperCase())}</div>
                            <div class="sender-info-text">
                                <div class="sender-title-line">
                                    <span class="full-sender-name clickable-author" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.composeToSender(this.dataset.messageId);}, [])} title="Redactar nuevo mensaje a ${Utils.sanitizeHTML(msg.senderName)}">
                                        ${Utils.sanitizeHTML(msg.senderName)}
                                        <i class="fas fa-paper-plane author-compose-icon"></i>
                                    </span>
                                </div>
                                <div class="sender-meta-line">
                                    <span class="receiver-chip clickable-author" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.composeToRecipient(this.dataset.messageId);}, [])} title="Redactar nuevo mensaje a ${Utils.sanitizeHTML(msg.receiverName)}"><i class="fas fa-user-circle"></i> para <strong>${Utils.sanitizeHTML(msg.receiverName)}</strong></span>
                                    <span class="meta-separator">•</span>
                                    <span class="date-chip"><i class="far fa-clock"></i> ${date}</span>
                                </div>
                            </div>
                        </div>
                        <div class="msg-reading-actions">
                            ${this.currentFolder === 'trash' ? `
                                <button class="btn btn-secondary btn-sm" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.restoreMessage(this.dataset.messageId);}, [])} title="Restaurar este mensaje a su bandeja original">
                                    <i class="fas fa-undo"></i> Restaurar
                                </button>
                                <button class="btn btn-outline-danger btn-sm" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.deletePermanently(this.dataset.messageId);}, [])} title="Eliminar definitivamente">
                                    <i class="fas fa-trash-alt"></i> Eliminar
                                </button>
                            ` : `
                                <button class="btn btn-primary-reply btn-sm" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.replyTo(this.dataset.messageId);}, [])}>
                                    <i class="fas fa-reply"></i> Responder
                                </button>
                                <button class="btn btn-outline-danger btn-sm" data-message-id="${Utils.sanitizeHTML(msg.id)}" ${UIEvents.attr("click", function(event,captured){MessagesView.deleteMessage(this.dataset.messageId);}, [])} title="Mover a la papelera">
                                    <i class="fas fa-trash-alt"></i> Eliminar
                                </button>
                            `}
                        </div>
                    </div>
                </div>
                <div class="content-body">${Utils.sanitizeHTML(msg.content)}</div>
                ${this.renderAttachments(msg.attachments)}
            </div>
        `;
    },

    // Searches the loaded messages of the open folder (sender, subject or text).
    setSearch(value) {
        this.searchQuery = String(value || '');
        const list = document.querySelector('.message-items-container');
        if (list) SafeHTML.set(list, this.renderMessageItemsOnly());
    },

    // «Directorio de contactos» in the folders column: a new message with the directory open.
    openDirectoryFromSidebar() {
        if (!this.isComposing) this.openComposeModal();
        setTimeout(() => this.openContactDirectory(), 0);
    },

    getFolderTitle() {
        const titles = {
            'inbox': 'Bandeja de Entrada',
            'sent': 'Enviados',
            'important': 'Destacados',
            'trash': 'Papelera'
        };
        return titles[this.currentFolder] || 'Mensajes';
    },

    switchFolder(folder) {
        // Si se está redactando, no permitir cambiar de bandeja directamente
        // desde el sidebar — antes se descartaba el borrador sin avisar (el
        // sidebar de carpetas sigue visible junto al formulario a pantalla
        // completa mientras isComposing es true, ver render()). Hay que
        // terminar de enviar o pulsar "Cancelar" en el propio formulario
        // (ver cancelCompose()) antes de poder moverse a otra carpeta.
        if (this.isComposing) {
            Utils.showToast('Termina o cancela el correo que estás redactando antes de cambiar de bandeja', 'warning');
            return;
        }
        this.currentFolder = folder;
        this.selectedMessageId = null;
        this.searchQuery = '';
        this.refresh();
    },

    selectMessage(id) {
        // Phone: no room for list and message side by side (messages.css), read it full screen.
        if (window.matchMedia && window.matchMedia('(max-width: 760px)').matches) return this.openFullView(id);
        this.selectedMessageId = id;
        this.isReadingFull = false;
        const msg = DataManager.getMessage(id);
        const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (msg && currentUser && msg.receiverId === currentUser.username && !msg.read) {
            DataManager.markAsRead(id);
        }
        this.refresh(false);
        // Update header unread count
        if (typeof App !== 'undefined' && App.setupHeaderInfo) {
            App.setupHeaderInfo();
        }
    },

    openFullView(id) {
        this.selectedMessageId = id;
        this.isReadingFull = true;
        const msg = DataManager.getMessage(id);
        const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        if (msg && currentUser && msg.receiverId === currentUser.username && !msg.read) {
            DataManager.markAsRead(id);
        }
        this.refresh(false);
        if (typeof App !== 'undefined' && App.setupHeaderInfo) {
            App.setupHeaderInfo();
        }
    },

    closeFullView() {
        this.isReadingFull = false;
        this.refresh(false);
    },

    async refresh(sync = true) {
        if (sync && typeof FileStorage !== 'undefined' && FileStorage.syncMessages) {
            await FileStorage.syncMessages();
        }
        const contentArea = document.getElementById('content-area');
        if (contentArea && App.currentView === 'mensajes') {
            // El re-render sustituye todo el HTML (incluida la lista de mensajes), lo que
            // reinicia su scroll a 0 — al hacer clic en un mensaje más abajo en la lista,
            // se veía como un salto hacia arriba aunque el mensaje correcto quedara
            // seleccionado. Se guarda y restaura el scroll de la lista para evitarlo.
            const listContainer = contentArea.querySelector('.message-items-container');
            const savedScrollTop = listContainer ? listContainer.scrollTop : 0;
            SafeHTML.set(contentArea, this.render());
            this.attachEventListeners();
            const newListContainer = contentArea.querySelector('.message-items-container');
            if (newListContainer) newListContainer.scrollTop = savedScrollTop;
        }
    },

    openComposeModal() {
        this.isComposing = true;
        this.isReadingFull = false;
        this.selectedMessageId = null;
        this.currentAttachments = [];
        this.refresh();
    },

    renderComposeForm() {
        return `
            <div class="message-content">
                ${this.renderComposeInner('file-input', 'attachments-preview')}
            </div>
        `;
    },

    renderComposeFormFullWidth() {
        return `
            <div class="compose-full-width">
                ${this.renderComposeInner('file-input-full', 'attachments-preview-full')}
            </div>
        `;
    },

    renderComposeInner(fileInputId, attachmentsPreviewId) {
        return `
            <div class="compose-form-container">
                <div class="compose-topbar">
                    <h1 class="subject-large">Redactar Nuevo Mensaje</h1>
                    <div class="compose-topbar-actions">
                        <button type="button" id="compose-send-button" class="btn btn-primary">
                            <i class="fas fa-paper-plane" aria-hidden="true"></i> <span id="compose-send-label">Enviar</span>
                        </button>
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){MessagesView.cancelCompose();}, [])}>
                            <i class="fas fa-times"></i> Cancelar
                        </button>
                    </div>
                </div>

                <form id="compose-form" class="compose-form">
                    <div class="compose-card">
                        <div class="compose-field-row">
                            <label class="form-label">Para</label>
                            <div class="input-group compose-recipient-group">
                                <span class="input-group-text contact-directory-trigger" ${UIEvents.attr("click", function(event,captured){MessagesView.openContactDirectory();}, [])} title="Abrir agenda de contactos">
                                    <i class="fas fa-user"></i>
                                </span>
                                <div class="recipient-autocomplete-container">
                                    <input type="text" id="recipient-search" class="form-control recipient-search-input" placeholder="Escribe el nombre del destinatario..." autocomplete="off" required>
                                    <input type="hidden" name="receiverId" id="recipient-id-input" required>
                                    <div id="recipient-autocomplete-dropdown" class="autocomplete-dropdown" style="display: none;"></div>
                                </div>
                            </div>
                        </div>

                        <div class="compose-divider"></div>

                        <div class="compose-field-row">
                            <label class="form-label">Asunto</label>
                            <input type="text" class="form-control" name="subject" placeholder="¿De qué trata el mensaje?" required>
                        </div>

                        <div class="compose-divider"></div>

                        <div class="compose-field-block">
                            <div class="compose-field-block-head">
                                <label class="form-label">Mensaje</label>
                                <div class="compose-field-block-actions">
                                    <div id="${attachmentsPreviewId}" class="attachments-preview">
                                        ${this.renderAttachmentsPreview()}
                                    </div>
                                    <button type="button" class="btn btn-sm btn-soft-primary compose-attach-btn" disabled title="Adjuntos desactivados hasta disponer de almacenamiento seguro">
                                        <i class="fas fa-paperclip"></i> Adjuntos no disponibles
                                    </button>
                                    <input type="file" id="${fileInputId}" multiple style="display: none;" ${UIEvents.attr("change", function(event,captured){MessagesView.handleFileSelect(event);}, [])}>
                                </div>
                            </div>
                            <div class="editor-toolbar">
                                <button type="button" class="btn btn-sm btn-ghost" title="Negrita"><i class="fas fa-bold"></i></button>
                                <button type="button" class="btn btn-sm btn-ghost" title="Cursiva"><i class="fas fa-italic"></i></button>
                                <button type="button" class="btn btn-sm btn-ghost" title="Lista"><i class="fas fa-list-ul"></i></button>
                                <button type="button" class="btn btn-sm btn-ghost" title="Enlace"><i class="fas fa-link"></i></button>
                            </div>
                            <textarea class="form-control compose-textarea" name="content" rows="12" placeholder="Escribe aquí tu mensaje..." required></textarea>
                        </div>
                    </div>
                </form>
            </div>
        `;
    },

    cancelCompose() {
        this.isComposing = false;
        this.currentAttachments = [];
        this.currentFolder = 'inbox';
        this.refresh();
    },

    renderFullContent() {
        if (!this.selectedMessageId) return '';
        const msg = DataManager.getMessage(this.selectedMessageId);
        if (!msg) return '';

        return `
            <div class="message-content message-content-full">
                <div class="message-scroll-area">
                    <button class="btn btn-sm btn-ghost message-back-btn" ${UIEvents.attr("click", function(event,captured){MessagesView.closeFullView();}, [])}>
                        <i class="fas fa-arrow-left"></i> Volver a la lista
                    </button>
                    ${this.renderMessageCard(msg)}
                </div>
            </div>
        `;
    },

    attachEventListeners() {
        // Setup manual draggable column resizers
        this.setupResizers();

        // Handle compose form submission
        const composeForm = document.getElementById('compose-form');
        if (composeForm) {
            const sendButton = document.getElementById('compose-send-button');
            const sendLabel = document.getElementById('compose-send-label');
            // The topbar is outside the form. Bind in trusted code because the
            // HTML sanitizer deliberately removes external form associations.
            if (sendButton) sendButton.onclick = () => {
                if (!this._sending) composeForm.requestSubmit();
            };
            composeForm.onsubmit = async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const currentUser = AuthManager.getCurrentUser();
                const receiverUsername = formData.get('receiverId');
                
                if (!receiverUsername) {
                    Utils.showToast('Por favor, selecciona un destinatario válido de la lista', 'error');
                    return;
                }
                
                const receiver = AuthManager.users.find(u => u.username === receiverUsername);

                if (this._sending) return;
                this._sending = true;
                if (sendButton) {
                    sendButton.disabled = true;
                    sendButton.setAttribute('aria-busy', 'true');
                }
                if (sendLabel) sendLabel.textContent = 'Enviando…';
                try {
                await DataManager.sendMessage({
                    senderId: currentUser.username,
                    senderName: currentUser.name,
                    receiverId: receiverUsername,
                    receiverName: receiver ? receiver.name : 'Usuario',
                    subject: formData.get('subject'),
                    content: formData.get('content'),
                    attachments: this.currentAttachments
                });

                this.currentAttachments = [];

                Utils.showToast('Mensaje enviado correctamente', 'success');
                this.isComposing = false;
                this.currentFolder = 'inbox';
                this.refresh();
                } catch (error) {
                    Utils.showToast(error.message || 'No se pudo enviar el mensaje', 'error');
                } finally {
                    this._sending = false;
                    if (sendButton) {
                        sendButton.disabled = false;
                        sendButton.removeAttribute('aria-busy');
                    }
                    if (sendLabel) sendLabel.textContent = 'Enviar';
                }
            };

            // Initialize Recipient Autocomplete
            const searchInput = document.getElementById('recipient-search');
            const hiddenInput = document.getElementById('recipient-id-input');
            const dropdown = document.getElementById('recipient-autocomplete-dropdown');

            if (searchInput && hiddenInput && dropdown) {
                const users = AuthManager.users || [];
                const currentUser = AuthManager.getCurrentUser();
                const recipientOptions = users.filter(u => u.username !== currentUser.username);
                let activeIndex = -1;
                let currentMatches = [];

                const updateDropdown = () => {
                    const query = searchInput.value.toLowerCase().trim();
                    SafeHTML.set(dropdown, '');
                    activeIndex = -1;

                    if (query === '') {
                        currentMatches = [];
                        dropdown.style.display = 'none';
                        return;
                    }

                    currentMatches = recipientOptions.filter(u => 
                        u.name.toLowerCase().includes(query) || 
                        u.role.toLowerCase().includes(query) ||
                        u.username.toLowerCase().includes(query)
                    );

                    if (currentMatches.length === 0) {
                        SafeHTML.set(dropdown, `<div class="autocomplete-no-results">No se encontraron destinatarios</div>`);
                    } else {
                        SafeHTML.set(dropdown, currentMatches.map((u, index) => {
                            const avatarChar = u.name ? u.name.charAt(0).toUpperCase() : '?';
                            return `
                                <div class="autocomplete-item" data-index="${index}" data-username="${Utils.sanitizeHTML(u.username)}">
                                    <div class="autocomplete-item-avatar">${Utils.sanitizeHTML(avatarChar)}</div>
                                    <div class="autocomplete-item-info">
                                        <span class="autocomplete-item-name">${Utils.sanitizeHTML(u.name)}</span>
                                        <span class="autocomplete-item-role">${Utils.sanitizeHTML(u.role.toUpperCase())}</span>
                                    </div>
                                </div>
                            `;
                        }).join(''));

                        // Add click listeners to items
                        dropdown.querySelectorAll('.autocomplete-item').forEach(item => {
                            item.addEventListener('click', () => {
                                const index = parseInt(item.getAttribute('data-index'));
                                selectRecipient(currentMatches[index]);
                            });
                        });
                    }
                    dropdown.style.display = 'block';
                };

                const selectRecipient = (user) => {
                    searchInput.value = `${user.name} (${user.role.toUpperCase()})`;
                    hiddenInput.value = user.username;
                    dropdown.style.display = 'none';
                    currentMatches = [];
                    activeIndex = -1;
                };

                searchInput.addEventListener('input', () => {
                    hiddenInput.value = ''; // Clear selected id as user is typing
                    updateDropdown();
                });

                searchInput.addEventListener('focus', () => {
                    if (searchInput.value.includes('(')) {
                        // If they already selected someone, and they focus, clear it to allow typing again
                        searchInput.value = '';
                        hiddenInput.value = '';
                    }
                    updateDropdown();
                });

                // Keyboard navigation
                searchInput.addEventListener('keydown', (e) => {
                    if (dropdown.style.display === 'none') return;

                    const items = dropdown.querySelectorAll('.autocomplete-item');
                    
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        if (activeIndex < currentMatches.length - 1) {
                            activeIndex++;
                            updateActiveItem(items);
                        }
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        if (activeIndex > 0) {
                            activeIndex--;
                            updateActiveItem(items);
                        }
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (activeIndex >= 0 && activeIndex < currentMatches.length) {
                            selectRecipient(currentMatches[activeIndex]);
                        }
                    } else if (e.key === 'Escape') {
                        dropdown.style.display = 'none';
                    }
                });

                const updateActiveItem = (items) => {
                    items.forEach(item => item.classList.remove('active'));
                    if (activeIndex >= 0 && activeIndex < items.length) {
                        const activeItem = items[activeIndex];
                        activeItem.classList.add('active');
                        activeItem.scrollIntoView({ block: 'nearest' });
                    }
                };

                // Close dropdown when clicking outside
                document.addEventListener('click', (e) => {
                    if (!searchInput.contains(e.target) && !dropdown.contains(e.target)) {
                        dropdown.style.display = 'none';
                    }
                });
            }
        }
    },

    setupResizers() {
        const sidebarResizer = document.getElementById('resizer-sidebar');
        const listResizer = document.getElementById('resizer-list');
        const sidebar = document.querySelector('.messages-sidebar');
        const list = document.querySelector('.messages-list');
        const container = document.querySelector('.messages-container');

        // Resizer 1: Sidebar (Folders)
        if (sidebarResizer && sidebar) {
            sidebarResizer.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = sidebar.getBoundingClientRect().width;

                sidebarResizer.classList.add('is-dragging');
                document.body.classList.add('messages-resizing');

                const onMouseMove = (moveEvent) => {
                    const deltaX = moveEvent.clientX - startX;
                    let newWidth = Math.round(startWidth + deltaX);
                    // Min 140px, Max 380px
                    newWidth = Math.max(140, Math.min(newWidth, 380));

                    sidebar.style.width = `${newWidth}px`;
                    sidebar.style.minWidth = `${newWidth}px`;
                    sidebar.style.maxWidth = `${newWidth}px`;
                    this.sidebarWidth = newWidth;
                };

                const onMouseUp = () => {
                    sidebarResizer.classList.remove('is-dragging');
                    document.body.classList.remove('messages-resizing');
                    BrowserState.setItem('messages_sidebar_width', this.sidebarWidth);
                    document.removeEventListener('mousemove', onMouseMove);
                    document.removeEventListener('mouseup', onMouseUp);
                };

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
            });

            // Double click to reset sidebar width
            sidebarResizer.addEventListener('dblclick', () => {
                this.sidebarWidth = 200;
                sidebar.style.width = '200px';
                sidebar.style.minWidth = '200px';
                sidebar.style.maxWidth = '200px';
                BrowserState.setItem('messages_sidebar_width', '200');
            });
        }

        // Resizer 2: Message List
        if (listResizer && list) {
            listResizer.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = list.getBoundingClientRect().width;

                listResizer.classList.add('is-dragging');
                document.body.classList.add('messages-resizing');

                const onMouseMove = (moveEvent) => {
                    const deltaX = moveEvent.clientX - startX;
                    let newWidth = Math.round(startWidth + deltaX);
                    // Calculate available space to ensure reading panel keeps at least 320px
                    const containerWidth = container ? container.getBoundingClientRect().width : window.innerWidth;
                    const sidebarCurrentWidth = sidebar ? sidebar.getBoundingClientRect().width : 200;
                    const maxAllowedWidth = Math.max(300, containerWidth - sidebarCurrentWidth - 320);

                    // Min 200px, Max bounded by screen and 700px
                    newWidth = Math.max(200, Math.min(newWidth, Math.min(700, maxAllowedWidth)));

                    list.style.width = `${newWidth}px`;
                    list.style.minWidth = `${newWidth}px`;
                    list.style.maxWidth = `${newWidth}px`;
                    this.listWidth = newWidth;
                };

                const onMouseUp = () => {
                    listResizer.classList.remove('is-dragging');
                    document.body.classList.remove('messages-resizing');
                    BrowserState.setItem('messages_list_width', this.listWidth);
                    document.removeEventListener('mousemove', onMouseMove);
                    document.removeEventListener('mouseup', onMouseUp);
                };

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
            });

            // Double click to reset list width
            listResizer.addEventListener('dblclick', () => {
                this.listWidth = 320;
                list.style.width = '320px';
                list.style.minWidth = '320px';
                list.style.maxWidth = '320px';
                BrowserState.setItem('messages_list_width', '320');
            });
        }
    },

    replyTo(messageId) {
        const msg = DataManager.getMessage(messageId);
        if (!msg) return;

        // Custom compose with reply data
        this.openComposeModal();

        setTimeout(() => {
            const form = document.getElementById('compose-form');
            if (form) {
                form.elements.receiverId.value = msg.senderId;
                const searchInput = document.getElementById('recipient-search');
                if (searchInput) {
                    const receiver = AuthManager.users.find(u => u.username === msg.senderId);
                    if (receiver) {
                        searchInput.value = `${receiver.name} (${receiver.role.toUpperCase()})`;
                    }
                }
                form.elements.subject.value = `Re: ${msg.subject}`;
                form.elements.content.focus();
            }
        }, 100);
    },

    composeToSender(messageId) {
        const msg = DataManager.getMessage(messageId);
        if (!msg || !msg.senderId) return;
        this.composeToUser(msg.senderId, msg.senderName);
    },

    composeToRecipient(messageId) {
        const msg = DataManager.getMessage(messageId);
        if (!msg || !msg.receiverId) return;
        this.composeToUser(msg.receiverId, msg.receiverName);
    },

    composeToUser(userId, displayName = null) {
        if (!userId) return;

        this.openComposeModal();

        setTimeout(() => {
            const form = document.getElementById('compose-form');
            if (form) {
                if (form.elements.receiverId) {
                    form.elements.receiverId.value = userId;
                }
                const hiddenInput = document.getElementById('recipient-id-input');
                if (hiddenInput) {
                    hiddenInput.value = userId;
                }
                const searchInput = document.getElementById('recipient-search');
                if (searchInput) {
                    let label = displayName;
                    if (typeof AuthManager !== 'undefined' && AuthManager.users) {
                        const user = AuthManager.users.find(u => u.username === userId || u.id === userId);
                        if (user) {
                            label = `${user.name} (${(user.role || '').toUpperCase()})`;
                        }
                    }
                    searchInput.value = label || userId;
                }
                if (form.elements.subject) {
                    form.elements.subject.value = '';
                    form.elements.subject.focus();
                }
            }
        }, 100);
    },

    deleteMessage(id) {
        if (this.currentFolder === 'trash') {
            this.deletePermanently(id);
            return;
        }
        const success = DataManager.moveToTrash ? DataManager.moveToTrash(id) : DataManager.deleteMessage(id);
        if (success) {
            if (this.selectedMessageId === id) {
                this.selectedMessageId = null;
                this.isReadingFull = false;
            }
            this.refresh(false);
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast('Mensaje movido a la papelera', 'info');
            }
        }
    },

    restoreMessage(id) {
        const success = DataManager.restoreMessage(id);
        if (success) {
            if (this.selectedMessageId === id) {
                this.selectedMessageId = null;
                this.isReadingFull = false;
            }
            this.refresh(false);
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast('Mensaje restaurado a su bandeja original', 'success');
            }
        }
    },

    deletePermanently(id) {
        Utils.showConfirm('¿Estás seguro de que deseas eliminar definitivamente este mensaje? No podrás recuperarlo.', () => {
            const success = DataManager.permanentDeleteMessage(id);
            if (success) {
                if (this.selectedMessageId === id) {
                    this.selectedMessageId = null;
                    this.isReadingFull = false;
                }
                this.refresh(false);
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast('Mensaje eliminado definitivamente', 'info');
                }
            }
        });
    },

    emptyTrash() {
        Utils.showConfirm('¿Estás seguro de que deseas vaciar toda la papelera? Esta acción no se puede deshacer.', () => {
            const success = DataManager.emptyTrash();
            if (success) {
                this.selectedMessageId = null;
                this.isReadingFull = false;
                this.refresh(false);
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast('Papelera vaciada correctamente', 'info');
                }
            } else {
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast('La papelera ya está vacía', 'info');
                }
            }
        });
    },

    handleFileSelect(event) {
        const files = Array.from(event.target.files);
        const MAX_SIZE = 500 * 1024; // 500KB limit per file to avoid BrowserState quota issues

        files.forEach(file => {
            if (file.size > MAX_SIZE) {
                Utils.showToast(`El archivo ${Utils.sanitizeHTML(file.name)} es demasiado grande (Máx 500KB)`, 'error');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                this.currentAttachments.push({
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    data: e.target.result // Base64 string
                });
                this.updateAttachmentsPreview();
            };
            reader.readAsDataURL(file);
        });

        // Reset input
        event.target.value = '';
    },

    updateAttachmentsPreview() {
        const preview = document.getElementById('attachments-preview');
        const previewFull = document.getElementById('attachments-preview-full');

        if (preview) SafeHTML.set(preview, this.renderAttachmentsPreview());
        if (previewFull) SafeHTML.set(previewFull, this.renderAttachmentsPreview());
    },

    renderAttachmentsPreview() {
        if (this.currentAttachments.length === 0) return '';

        return this.currentAttachments.map((file, index) => `
            <div class="attachment-chip" style="display: flex; align-items: center; gap: 0.5rem; background: var(--dnr-teal-100); padding: 0.25rem 0.75rem; border-radius: 16px; font-size: 0.85rem;">
                <i class="fas fa-paperclip" style="color: var(--dnr-teal-700);"></i>
                <span style="max-width: 150px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${Utils.sanitizeHTML(file.name)}</span>
                <span style="color: var(--dnr-ink-mute); font-size: 0.75rem;">(${this.formatFileSize(file.size)})</span>
                <button type="button" ${UIEvents.attr("click", function(event,captured){MessagesView.removeAttachment(captured[0]);}, [index])} style="background: none; border: none; cursor: pointer; color: var(--dnr-ink-mute); padding: 0; margin-left: 0.25rem;">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        `).join('');
    },

    removeAttachment(index) {
        this.currentAttachments.splice(index, 1);
        this.updateAttachmentsPreview();
    },

    safeAttachmentUrl(value) {
        // Legacy inline attachments are download-only; never accept HTML, SVG or remote URLs.
        return typeof value === 'string' && /^data:(?:application\/(?:pdf|octet-stream)|image\/(?:png|jpeg|gif|webp)|text\/plain);base64,[A-Za-z0-9+/]*={0,2}$/.test(value) ? value : null;
    },

    renderAttachments(attachments) {
        if (!attachments || attachments.length === 0) return '';

        return `
            <div class="message-attachments" style="margin-top: 1rem; border-top: 1px solid var(--border-color); padding-top: 1rem;">
                <h4 style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 0.5rem;">Adjuntos (${attachments.length})</h4>
                <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
                    ${attachments.map(file => this.safeAttachmentUrl(file.data) ? `
                        <a href="${Utils.sanitizeHTML(this.safeAttachmentUrl(file.data))}" download="${Utils.sanitizeHTML(file.name)}" class="attachment-item" style="display: flex; align-items: center; gap: 0.75rem; background: var(--dnr-surface-sunken); border: 1px solid var(--dnr-line); padding: 0.5rem 1rem; border-radius: var(--dnr-r-sm); text-decoration: none; color: var(--text-primary); transition: all 0.2s;">
                            <div class="attachment-icon" style="color: var(--dnr-teal-700);">
                                <i class="fas ${this.getFileIcon(file.type)}"></i>
                            </div>
                            <div class="attachment-info" style="display: flex; flex-direction: column;">
                                <span style="font-weight: 500; font-size: 0.9rem;">${Utils.sanitizeHTML(file.name)}</span>
                                <span style="font-size: 0.8rem; color: var(--text-tertiary);">${this.formatFileSize(file.size)}</span>
                            </div>
                            <div class="attachment-action">
                                <i class="fas fa-download"></i>
                            </div>
                        </a>
                    ` : `<span class="attachment-item">${Utils.sanitizeHTML(file.name)} — Adjunto no disponible</span>`).join('')}
                </div>
            </div>
        `;
    },

    formatFileSize(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    getFileIcon(type) {
        type = String(type || '');
        if (type.includes('image')) return 'fa-file-image';
        if (type.includes('pdf')) return 'fa-file-pdf';
        if (type.includes('word') || type.includes('document')) return 'fa-file-word';
        if (type.includes('excel') || type.includes('sheet')) return 'fa-file-excel';
        return 'fa-file-alt';
    },

    // ─── Agenda de contactos (design B2, Oct 2026): the search box looks in every group;
    // «Mis alumnos» / «Mis profesores», what each professor teaches, the last message with
    // each person and Recientes; ↑ ↓ Intro Esc. Choosing a contact fills «Para».
    _dir: null,

    _directoryData() {
        const me = AuthManager.getCurrentUser();
        const subjects = typeof DataManager !== 'undefined' && DataManager.getSubjects ? DataManager.getSubjects() : [];
        const formations = new Map((typeof DataManager !== 'undefined' && DataManager.getFormations ? DataManager.getFormations() : []).map(f => [String(f.id), f]));
        const students = new Map((typeof StudentService !== 'undefined' ? StudentService.getStudents() : []).map(s => [String(s.id), s]));
        const fname = id => formations.get(String(id))?.name || '';
        // Last message exchanged with each username.
        const last = new Map();
        (typeof DataManager !== 'undefined' && DataManager.getMessages ? DataManager.getMessages() : []).forEach(m => {
            const other = m.senderId === me.username ? m.receiverId : m.receiverId === me.username ? m.senderId : null;
            if (!other) return;
            const t = new Date(m.date).getTime();
            if (!(last.get(other) > t)) last.set(other, t);
        });
        const teaches = pid => subjects.filter(s => pid && String(s.professorId) === String(pid));
        const studentFormations = u => (students.get(String(u.studentId))?.formations || []).map(String);
        const roleLabels = { admin: 'Dirección', coordinator: 'Coordinación', rrhh: 'RRHH', warehouse: 'Almacén', professor: 'Profesor', student: 'Alumno' };
        const groupOf = u => ['admin', 'coordinator', 'rrhh', 'warehouse'].includes(u.role) ? 'admin' : u.role === 'professor' ? 'professors' : u.role === 'student' ? 'students' : 'admin';
        // Formations of the person writing: where they teach, or where they study.
        const mine = me.role === 'professor' ? new Set(teaches(me.professorId).map(s => String(s.formationId)).filter(Boolean))
            : me.role === 'student' ? new Set(studentFormations(me)) : new Set();
        const people = (AuthManager.users || []).filter(u => u.username && u.username !== me.username).map(u => {
            const group = groupOf(u);
            let sub = '', forms = [];
            if (group === 'professors') {
                const list = teaches(u.professorId);
                forms = [...new Set(list.map(s => String(s.formationId)).filter(Boolean))];
                const names = [...new Set(list.map(s => s.name).filter(Boolean))];
                sub = [names.slice(0, 3).join(' · ') + (names.length > 3 ? ` +${names.length - 3}` : ''), forms.length === 1 ? fname(forms[0]) : forms.length > 1 ? `${forms.length} formaciones` : ''].filter(Boolean).join(' · ') || 'Profesor';
            } else if (group === 'students') {
                forms = studentFormations(u);
                sub = [forms.map(fname).filter(Boolean).join(', '), `@${u.username}`].filter(Boolean).join(' · ');
            } else {
                sub = `${roleLabels[u.role] || u.role || ''} · @${u.username}`;
            }
            return { username: u.username, name: u.name || u.username, role: u.role, roleLabel: roleLabels[u.role] || u.role || '', group, sub, forms, last: last.get(u.username) || 0,
                mine: !!mine.size && forms.some(f => mine.has(f)) && (me.role === 'professor' ? group === 'students' : group === 'professors'),
                text: Utils.normalizeString(`${u.name || ''} ${u.username} ${roleLabels[u.role] || ''} ${sub} ${forms.map(fname).join(' ')}`) };
        }).sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        return { people, mineLabel: me.role === 'professor' ? 'Mis alumnos' : me.role === 'student' ? 'Mis profesores' : '', formations: [...formations.values()] };
    },

    _ago(t) {
        if (!t) return '';
        const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(t).setHours(0, 0, 0, 0)) / 86400000);
        return days <= 0 ? 'Hoy' : days === 1 ? 'Ayer' : days < 7 ? `Hace ${days} días` : new Date(t).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    },

    openContactDirectory() {
        const data = this._directoryData();
        const hasMine = !!data.mineLabel && data.people.some(p => p.mine);
        this._dir = { ...data, tab: hasMine ? 'mine' : 'all', q: '', formation: '', active: 0, hasMine };
        const contentHtml = `
            <div class="contact-directory-modal dnr-proposal cdir">
                <div class="cdir-top">
                    <div class="cdir-search">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <input type="search" id="contact-directory-search" data-search-clear="off" placeholder="Busca a cualquiera: nombre, usuario, rol, formación o asignatura" aria-label="Buscar contacto" autocomplete="off"
                            role="combobox" aria-controls="cdir-list" aria-expanded="true">
                    </div>
                    <div class="cdir-filters">
                        <div class="cdir-seg" id="cdir-tabs" role="tablist" aria-label="Grupo"></div>
                        <select class="cdir-select" id="cdir-formation" aria-label="Formación" ${UIEvents.attr("change", function(event,captured){MessagesView.setDirectoryFormation(this.value);}, [])}>
                            <option value="">Todas las formaciones</option>
                            ${data.formations.filter(f => data.people.some(p => p.forms.includes(String(f.id)))).sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'))
                                .map(f => `<option value="${Utils.sanitizeHTML(f.id)}">${Utils.sanitizeHTML(f.name)} · ${data.people.filter(p => p.forms.includes(String(f.id))).length}</option>`).join('')}
                        </select>
                    </div>
                </div>
                <div id="cdir-body" class="cdir-body"></div>
                <div class="cdir-foot"><span id="contact-directory-count"></span><span class="cdir-keys">↑ ↓ moverte · Intro elegir · Esc cerrar</span></div>
            </div>`;
        ModalManager.show('Agenda de contactos', contentHtml, []);
        const modalContainer = document.querySelector('#modal-overlay .modal-container');
        const modalBody = document.getElementById('modal-body');
        const modalActions = document.getElementById('modal-actions');
        if (modalContainer) modalContainer.classList.add('contact-directory-modal-container', 'cdir-container');
        if (modalBody) { modalBody.style.padding = '0'; modalBody.style.margin = '0'; modalBody.style.overflow = 'hidden'; }
        if (modalActions) { modalActions.style.display = 'none'; }
        this.paintDirectory();
        const input = document.getElementById('contact-directory-search');
        if (input) {
            input.focus();
            input.addEventListener('input', () => { this._dir.q = input.value; this._dir.active = 0; this.paintDirectory(); });
            input.addEventListener('keydown', e => {
                const shown = this._shownContacts();
                if (e.key === 'Escape') { e.preventDefault(); ModalManager.close(); }
                else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (!shown.length) return;
                    this._dir.active = (this._dir.active + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
                    this.paintDirectory();
                    document.querySelector('.cdir-person.is-active')?.scrollIntoView({ block: 'nearest' });
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const p = shown[this._dir.active];
                    if (p) this.selectContactFromDirectory(p.username, p.name, p.roleLabel);
                }
            });
        }
    },

    // Contacts listed now, in display order (groups, then name). A search ignores the tab.
    _shownContacts() {
        const d = this._dir;
        if (!d) return [];
        const q = Utils.normalizeString(d.q || '').trim();
        const order = { admin: 0, professors: 1, students: 2 };
        return d.people.filter(p => (q ? true : d.tab === 'all' || (d.tab === 'mine' ? p.mine : p.group === d.tab)))
            .filter(p => !d.formation || p.forms.includes(d.formation))
            .filter(p => !q || p.text.includes(q))
            .sort((a, b) => order[a.group] - order[b.group]);
    },

    paintDirectory() {
        const d = this._dir, body = document.getElementById('cdir-body');
        if (!d || !body) return;
        const q = (d.q || '').trim();
        const count = g => d.people.filter(p => g === 'all' || (g === 'mine' ? p.mine : p.group === g)).length;
        const tabs = [['all', 'Todos'], ...(d.hasMine ? [['mine', d.mineLabel]] : []), ['admin', 'Administración'], ['professors', 'Profesorado'], ['students', 'Alumnado']];
        SafeHTML.set(document.getElementById('cdir-tabs'), tabs.map(([id, label]) => `<button type="button" role="tab" class="${!q && d.tab === id ? 'is-on' : ''}" aria-selected="${!q && d.tab === id}"
            ${UIEvents.attr("click", function(event,captured){MessagesView.setDirectoryTab(String(captured[0]));}, [id])}>${Utils.sanitizeHTML(label)} · ${count(id)}</button>`).join(''));
        const shown = this._shownContacts();
        if (d.active >= shown.length) d.active = 0;
        const recent = !q ? d.people.filter(p => p.last).sort((a, b) => b.last - a.last).slice(0, 5) : [];
        const names = { admin: 'Administración', professors: 'Profesorado', students: 'Alumnado' };
        const groups = [];
        shown.forEach((p, i) => {
            const label = !q && d.tab === 'mine' ? d.mineLabel : names[p.group];
            if (!groups.length || groups[groups.length - 1].label !== label) groups.push({ label, list: [] });
            groups[groups.length - 1].list.push([p, i]);
        });
        const initial = name => Utils.sanitizeHTML(String(name || '?').trim().charAt(0).toUpperCase());
        const pick = p => UIEvents.attr("click", function(event,captured){MessagesView.selectContactFromDirectory(String(captured[0]),String(captured[1]),String(captured[2]));}, [p.username, p.name, p.roleLabel]);
        SafeHTML.set(body, `
            ${recent.length ? `<div class="cdir-recent"><span class="cdir-label">Recientes</span><div class="cdir-chips">${recent.map(p => `<button type="button" class="cdir-chip" title="Último mensaje: ${Utils.sanitizeHTML(this._ago(p.last).toLowerCase())}" ${pick(p)}><span class="cdir-avatar is-small">${initial(p.name)}</span>${Utils.sanitizeHTML(p.name)}</button>`).join('')}</div></div>` : ''}
            <div class="cdir-list" id="cdir-list" role="listbox" aria-label="Contactos">
                ${groups.map(g => `<span class="cdir-label cdir-group">${Utils.sanitizeHTML(g.label)} · ${g.list.length}</span>
                    ${g.list.map(([p, i]) => `<button type="button" role="option" class="cdir-person contact-card ${i === d.active ? 'is-active' : ''}" aria-selected="${i === d.active}" ${pick(p)}>
                        <span class="cdir-avatar">${initial(p.name)}</span>
                        <span class="cdir-person-main"><b class="contact-name">${Utils.sanitizeHTML(p.name)}</b><small>${Utils.sanitizeHTML(p.sub)}</small></span>
                        ${p.last ? `<span class="cdir-last">${p.last && this._ago(p.last) === 'Hoy' ? 'Le escribiste hoy' : p.last && this._ago(p.last) === 'Ayer' ? 'Le escribiste ayer' : Utils.sanitizeHTML(this._ago(p.last))}</span>` : ''}
                    </button>`).join('')}`).join('')}
                ${shown.length ? '' : `<p class="cdir-empty">${q ? `No hay nadie con «${Utils.sanitizeHTML(q)}». Prueba con el apellido o el usuario.` : 'No hay contactos en este grupo.'}</p>`}
            </div>`);
        const countEl = document.getElementById('contact-directory-count');
        if (countEl) countEl.textContent = q ? `${shown.length} ${shown.length === 1 ? 'resultado' : 'resultados'} en todos los grupos` : `${shown.length} ${shown.length === 1 ? 'contacto' : 'contactos'}`;
    },

    setDirectoryTab(tab) {
        if (!this._dir) return;
        this._dir.tab = tab; this._dir.active = 0; this._dir.q = '';
        const input = document.getElementById('contact-directory-search');
        if (input) { input.value = ''; input.focus(); }
        this.paintDirectory();
    },

    setDirectoryFormation(value) {
        if (!this._dir) return;
        this._dir.formation = String(value || ''); this._dir.active = 0;
        this.paintDirectory();
    },

    selectContactFromDirectory(username, name, role) {
        const searchInput = document.getElementById('recipient-search');
        const hiddenInput = document.getElementById('recipient-id-input');
        if (searchInput && hiddenInput) {
            searchInput.value = `${name} (${String(role || '').toUpperCase()})`;
            hiddenInput.value = username;
        }
        ModalManager.close();
    }
};

window.MessagesView = MessagesView;
